import * as React from 'react'
import moment from 'moment'

import { useDrag } from 'react-dnd'
import { getEmptyImage } from 'react-dnd-html5-backend'

import { EventModel } from '@/app/models/newDelegation/eventModel'
import { CountryModel } from '@/app/models/timesheet/countryModel'
import { DelegationMarkerPopup } from '@/modules/drivers/tabs/businessTrips/popups/DelegationMarkerPopup'
import { eventKey } from '@/app/common/newDelegation/eventIdentity'

import DelegationMarkerVisual from './DelegationMarkerVisual'
import { toMovedEventPayload } from './markerDrag'

type DelegationMarkerEditBlockProps = {
  delegationMarker: EventModel
  xPosition: number
  lane?: number
  bottomPadding: number
  events: Array<EventModel>
  allPreviousTripEvents: Array<EventModel>
  translate: (text: string) => any
  countries: Array<CountryModel>
  readOnly: boolean
  current: boolean
  editEvent?: (event: EventModel, shouldEdit: boolean) => void
  addEvent?: (event: EventModel) => void
  removeEvents?: (events: Array<EventModel>) => void
  hideDelegationMarkerPopups?: (event: EventModel) => void
  markNewEvent?: (event: EventModel) => void
}

type DelegationMarkerEditBlockState = {
  isPopupOpen: boolean
  openedByClick: boolean
}

const delegationMarkerSource = {
  canDrag(props: DelegationMarkerEditBlockProps) {
    return !props.readOnly && props.current
  },

  isDragging(props: DelegationMarkerEditBlockProps, monitor: any) {
    return eventKey(monitor.getItem()) === eventKey(props.delegationMarker)
  },

  beginDrag(props: DelegationMarkerEditBlockProps) {
    const item: any = {
      ...props.delegationMarker,
      position: props.xPosition,
      translate: props.translate,
      isDragging: true,
    }
    item.isNew = false
    return item
  },

  endDrag(props: DelegationMarkerEditBlockProps, monitor: any) {
    const item = monitor.getItem()
    item.isDragging = false
    if (monitor.didDrop()) return

    if (item.rawDate && props.editEvent) {
      const date = moment(item.rawDate).clone().add(-1, 'seconds')
      props.editEvent(toMovedEventPayload(item, date), true)
    }
  },
}

class DelegationMarkerEditBlock extends React.Component<
  DelegationMarkerEditBlockProps & any,
  DelegationMarkerEditBlockState
> {
  constructor(props: DelegationMarkerEditBlockProps & any) {
    super(props)
    this.state = {
      isPopupOpen: false,
      openedByClick: false,
    }
  }

  componentDidMount() {
    const { previewRef, delegationMarker } = this.props
    previewRef(getEmptyImage(), { captureDraggingState: true })

    if (
      !this.props.readOnly &&
      this.props.current &&
      delegationMarker.isNew &&
      !delegationMarker.isDragging &&
      !this.props.isDragging
    ) {
      this.props.hideDelegationMarkerPopups(delegationMarker)
      this.setState({ isPopupOpen: true })
    }
  }


  componentDidUpdate(prevProps: DelegationMarkerEditBlockProps & any) {
    const previous = prevProps.delegationMarker
    const current = this.props.delegationMarker


    if (current.isNew && !previous.isNew && !this.state.isPopupOpen) {
      this.props.hideDelegationMarkerPopups(current)
      this.setState({ isPopupOpen: true, openedByClick: false })
      return
    }

    if (
      !current.isNew &&
      previous.isNew &&
      !current.isDragging &&
      this.state.isPopupOpen &&
      !this.state.openedByClick
    ) {
      // `openedByClick` has to be cleared alongside `isPopupOpen`, otherwise a
      // reused instance takes the `else` branch of closePopup and calls
      // markNewEvent instead of editEvent — silently dropping the new marker.
      this.setState({ isPopupOpen: false, openedByClick: false })
    }
  }

  private handleActivate = () => {
    const { delegationMarker } = this.props
    if (!this.props.current) return
    this.props.hideDelegationMarkerPopups(delegationMarker)
    this.props.markNewEvent({ ...delegationMarker, isNew: true })
    this.setState({ isPopupOpen: true, openedByClick: true })
  }

  render() {
    const {
      delegationMarker,
      xPosition,
      bottomPadding,
      translate,
      countries,
      events,
      allPreviousTripEvents,
      isDragging,
    } = this.props

    const id = 'dm_new_' + eventKey(delegationMarker)
    const lane = this.props.lane ?? 0

    return (
      <div
        ref={this.props.dragRef}
        style={{
          opacity: isDragging ? 0.5 : 1,
          position: 'absolute',
          left: xPosition - 15,
          top: 0,
          bottom: 0,
          paddingBottom: bottomPadding,
          zIndex: 120 + lane,
          pointerEvents: 'none',
        }}
      >
        <div id={id} className="tw-h-full">
          <DelegationMarkerVisual
            marker={delegationMarker}
            translate={translate}
            lane={lane}
            onActivate={this.handleActivate}
          />
        </div>

        {!isDragging && this.state.isPopupOpen && (
          <DelegationMarkerPopup
            id={id}
            readOnly={this.props.readOnly}
            countries={countries}
            events={events}
            delegationMarker={delegationMarker}
            allPreviousTripEvents={allPreviousTripEvents}
            isOpen={this.state.isPopupOpen}
            translate={translate}
            onDelete={(dm) => {
              this.setState({ isPopupOpen: false, openedByClick: false })
              this.props.removeEvents([dm])
            }}
            closePopup={(dm, shouldEdit, isChanged, newMarkers) => {
              if (
                !this.props.readOnly &&
                (!this.state.openedByClick || isChanged)
              ) {
             
                this.props.editEvent(
                  { ...dm, isNew: false },
                  shouldEdit || !!dm.isNew,
                )
                if (newMarkers != null && newMarkers.length > 0) {
                  for (const newMarker of newMarkers) {
                    this.props.editEvent(
                      { ...newMarker, isNew: false, lp: -1 },
                      shouldEdit,
                    )
                  }
                }
              } else {
                this.props.markNewEvent({ ...delegationMarker, isNew: false })
              }
              this.setState({ isPopupOpen: false, openedByClick: false })
            }}
          />
        )}
      </div>
    )
  }
}


const DelegationMarkerEditBlockDnd = (
  props: DelegationMarkerEditBlockProps & any,
) => {
  const [{ isDragging }, dragRef, previewRef] = useDrag({
    type: 'DELEGATION_MARKER',
    canDrag: () => delegationMarkerSource.canDrag(props),
    isDragging: (monitor) => delegationMarkerSource.isDragging(props, monitor),
    item: () => delegationMarkerSource.beginDrag(props),
    end: (_item, monitor) => delegationMarkerSource.endDrag(props, monitor),
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  })

  return (
    <DelegationMarkerEditBlock
      {...props}
      isDragging={isDragging}
      dragRef={dragRef}
      previewRef={previewRef}
    />
  )
}

export default DelegationMarkerEditBlockDnd
